import type { Component, VoidComponent } from "solid-js";
import { createSignal, Match, Show, Switch } from "solid-js";
import type { RouteSectionProps } from "@solidjs/router";
import withPagePermissionCheck from "../../../utils/withPagePermissionCheck";
import PermissionService from "../../../services/PermissionService";
import { useTranslation } from "../../../state/i18n";
import { TextAndIconButton } from "../../../components/button/Button";
import ContentSection from "../../formEditor/ContentSection";
import Pill from "../../../components/pill/Pill";
import SeparatorLine from "../../../components/SeparatorLine";
import ApiService from "../../../services/ApiService";
import { QueryBoundary } from "../../../utils/QueryBoundary";
import InlineMessage from "../../../components/inlineMessage/InlineMessage";
import { useCustomerState } from "../../../state/customer";
import type { SsoConnectionType, SsoState } from "../../../types/Sso";
import GenericFailureModal from "../../GenericFailureModal";
import { useModalManager } from "../../../components/modal/ModalManager";
import withPageProductFeatureCheck from "../../../utils/withPageProductFeatureCheck";
import { normalizeError } from "../../../MoreAppError";
import SettingsPageHeader from "./components/SettingsPageHeader.tsx";

const SsoPage: Component<RouteSectionProps<never>> = () => {
  const { t } = useTranslation();
  const customer = useCustomerState();
  const statusQuery = ApiService.getSsoStatus();

  const openConnectionSetup = async (type: SsoConnectionType) => {
    const returnUrl = `${window.location.origin}/customers/${customer.customerId}/settings/sso`;
    const response = await ApiService.getSsoConnectionConfigurationUrl(type, returnUrl);
    window.open(response.data.url, "_blank");
  };

  const ConfigurationStatusPill: VoidComponent<{ state: SsoState }> = (props) => (
    <Switch>
      <Match when={props.state === "unconfigured"}>
        <Pill variant="neutral" label={t(($) => $.SETTINGS_SSO_STATUS_UNCONFIGURED)} />
      </Match>
      <Match when={props.state === "active"}>
        <Pill variant="success" label={t(($) => $.SETTINGS_SSO_STATUS_ACTIVE)} />
      </Match>
      <Match when={props.state === "inactive"}>
        <Pill variant="warning" label={t(($) => $.SETTINGS_SSO_STATUS_INACTIVE)} />
      </Match>
    </Switch>
  );

  const ConfigureConnectionButton: VoidComponent<{ type: SsoConnectionType; isProvisioned: boolean }> = (props) => {
    const { open } = useModalManager();
    const [isLoading, setIsLoading] = createSignal(false);
    const provisionOrganizationMutation = ApiService.provisionOrganization();

    const handleConnectionSetup = async () => {
      try {
        setIsLoading(true);

        if (!props.isProvisioned) {
          await provisionOrganizationMutation.mutateAsync();
        }

        await openConnectionSetup(props.type);
      } catch (err) {
        open({
          content: () => <GenericFailureModal error={normalizeError(err)} />,
        });
      } finally {
        setIsLoading(false);
      }
    };

    return (
      <TextAndIconButton
        size="large"
        variant="secondary"
        text={t(($) => $.SETTINGS_SSO_CONNECTION_CONFIGURE_ACTION_LABEL)}
        icon="externalLink"
        loading={isLoading()}
        onClick={handleConnectionSetup}
      />
    );
  };

  return (
    <div class="flex h-full flex-col">
      <SettingsPageHeader
        title={t(($) => $.SETTINGS_SECTION_CONNECTIONS_LABEL)}
        description={t(($) => $.SETTINGS_ITEM_SSO_LABEL)}
      />

      <div class="h-full max-w-400 overflow-y-auto pt-8 pb-6">
        <ContentSection>
          <QueryBoundary>
            <Show when={statusQuery.data}>
              {(status) => (
                <div class="flex flex-col gap-6">
                  <div>
                    <h4 class="flex items-center gap-2 text-header-lg text-primary">
                      {t(($) => $.SETTINGS_SSO_SETUP_TITLE)} <ConfigurationStatusPill state={status().state} />
                    </h4>
                    <p class="text-base-regular text-secondary">{t(($) => $.SETTINGS_SSO_SETUP_DESCRIPTION)}</p>
                  </div>

                  <Show when={status().shared}>
                    <InlineMessage variant="warning" label={t(($) => $.SETTINGS_SSO_SHARED_WARNING)} />
                  </Show>

                  <SeparatorLine />

                  <div class="flex justify-between gap-1">
                    <div>
                      <h4 class="flex items-center gap-2 text-header-md text-primary">
                        {t(($) => $.SETTINGS_SSO_CONNECTION_SSO_TITLE)}{" "}
                        <ConfigurationStatusPill state={status().sso.state} />
                      </h4>
                      <p class="text-base-regular text-secondary">
                        {t(($) => $.SETTINGS_SSO_CONNECTION_SSO_DESCRIPTION)}
                      </p>
                    </div>

                    <ConfigureConnectionButton type="sso" isProvisioned={status().provisioned} />
                  </div>

                  <SeparatorLine />

                  <div class="flex items-center justify-between gap-1">
                    <div class="flex flex-col gap-2">
                      <div>
                        <h4 class="flex items-center gap-2 text-header-md text-primary">
                          {t(($) => $.SETTINGS_SSO_CONNECTION_DSYNC_TITLE)}{" "}
                          <ConfigurationStatusPill state={status().dsync.state} />
                        </h4>
                        <p class="text-base-regular text-secondary">
                          {t(($) => $.SETTINGS_SSO_CONNECTION_DSYNC_DESCRIPTION)}
                        </p>
                      </div>
                    </div>

                    <ConfigureConnectionButton type="dsync" isProvisioned={status().provisioned} />
                  </div>
                </div>
              )}
            </Show>
          </QueryBoundary>
        </ContentSection>
      </div>
    </div>
  );
};

const ProtectedSsoPage = withPageProductFeatureCheck(
  withPagePermissionCheck(SsoPage, PermissionService.canManageSSO),
  "SINGLE_SIGN_ON",
);

export default ProtectedSsoPage;
